import React, { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { useAuth } from './stores/authStore';

// ─────────────────────────────────────────────────────────────────────
// NotoLogic route map.
// Mirror of Worksnip's routes.tsx pattern: every page lazy-loaded, single
// <Suspense> wrapper, <Guard> HOC for auth.
//
// Phase 1 wires Login + Register + Dashboard + AppShell.
// Pages marked `// TODO Phase N` are written progressively — the placeholder
// <Stub /> keeps the dev server boot path working until they land.
// ─────────────────────────────────────────────────────────────────────

const Stub = ({ name }: { name: string }) => (
  <div className="min-h-screen flex items-center justify-center text-mn-ink-2">
    <div className="text-center max-w-md">
      <div
        className="font-mono text-xs uppercase text-mn-ink-3 mb-2"
        style={{ letterSpacing: 'var(--tracking-caps)' }}
      >
        Not built yet
      </div>
      <h1 className="text-2xl font-bold text-mn-ink mb-3">{name}</h1>
      <p className="text-sm text-mn-ink-3">
        This page is part of a later build phase. See <code>CLAUDE.md</code> §8.
      </p>
    </div>
  </div>
);

// Phase 1
const LoginPage     = lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage  = lazy(() => import('./pages/auth/RegisterPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const AppShell      = lazy(() => import('./components/layout/AppShell'));

// Workspace invitations — public accept page (no auth)
const AcceptInvitePage = lazy(() => import('./pages/AcceptInvitePage'));

// Phase 2
const ProjectsPage         = lazy(() => import('./pages/ProjectsPage'));
const ProjectDetailPage    = lazy(() => import('./pages/ProjectDetailPage'));
const ProjectMeetingsPage  = lazy(() => import('./pages/ProjectMeetingsPage'));
const MeetingsListPage     = lazy(() => import('./pages/MeetingsListPage'));
const MeetingUploadPage    = lazy(() => import('./pages/MeetingUploadPage'));

// Wave 10 — Tasks (cross-meeting actions + decisions)
const TasksPage            = lazy(() => import('./pages/TasksPage'));

// Phase 4 — the heart
const MeetingReportPage    = lazy(() => import('./pages/MeetingReportPage'));

// Phase 6 — sharing (G11). Real read-only public viewer.
const PublicMeetingViewer  = lazy(() => import('./pages/PublicMeetingViewer'));

// Phase 7+
const SearchPage           = lazy(() => import('./pages/SearchPage'));
const InboxPage            = lazy(() => import('./pages/InboxPage'));

// Wave 6 — Ask NotoLogic page (renamed from NaroChatPage per v2 prototype).
// The slide-over NaroChatPanel inside MeetingReportPage is unchanged.
const AskPage              = lazy(() => import('./pages/AskPage'));

// Phase 11 — three distinct admin surfaces, each with its OWN left panel:
//   /settings/*  — personal preferences        (everyone)
//   /admin/*     — workspace (org) admin        (org-admin+)
//   /platform/*  — cross-tenant platform admin  (super-admin)
const SettingsPage         = lazy(() => import('./pages/SettingsPage'));
const SettingsProfile      = lazy(() => import('./pages/settings/Profile'));
const SettingsAI           = lazy(() => import('./pages/settings/AI'));
const SettingsTTS          = lazy(() => import('./pages/settings/TTS'));
const SettingsNotifications = lazy(() => import('./pages/settings/Notifications'));
const SettingsSessions     = lazy(() => import('./pages/settings/Sessions'));
const SettingsIntegrations = lazy(() => import('./pages/settings/Integrations'));

// Workspace (org) admin — wired Settings* pages where they exist, Admin* extras.
const AdminLayout          = lazy(() => import('./pages/admin/AdminLayout'));
const AdminDashboard       = lazy(() => import('./pages/admin/AdminDashboard'));
const SettingsOrg          = lazy(() => import('./pages/settings/Org'));
const SettingsMembers      = lazy(() => import('./pages/settings/Members'));
const SettingsBilling      = lazy(() => import('./pages/settings/Billing'));
const SettingsAudit        = lazy(() => import('./pages/settings/Audit'));
const SettingsDeveloper    = lazy(() => import('./pages/settings/Developer'));
const AdminMeetings        = lazy(() => import('./pages/admin/AdminMeetings'));
const AdminUsers           = lazy(() => import('./pages/admin/AdminUsers'));
const AdminAISettings      = lazy(() => import('./pages/admin/AdminAISettings'));
const AdminRoles           = lazy(() => import('./pages/admin/AdminRoles'));
const AdminUsage           = lazy(() => import('./pages/admin/AdminUsage'));

// Platform admin (super-admin only — gated by PlatformLayout)
const PlatformLayout       = lazy(() => import('./pages/platform/PlatformLayout'));
const PlatformFeatureFlags  = lazy(() => import('./pages/settings/platform/FeatureFlags'));
const PlatformTTSProviders  = lazy(() => import('./pages/settings/platform/TTSProviders'));
const PlatformSTTProviders  = lazy(() => import('./pages/settings/platform/STTProviders'));
const PlatformEmbeddings    = lazy(() => import('./pages/settings/platform/Embeddings'));
const PlatformTenants      = lazy(() => import('./pages/admin/platform/Tenants'));
const PlatformAICost       = lazy(() => import('./pages/admin/platform/AICost'));
const PlatformHealth       = lazy(() => import('./pages/admin/platform/Health'));
const PlatformJobs         = lazy(() => import('./pages/admin/platform/Jobs'));
const PlatformPrompts      = lazy(() => import('./pages/admin/platform/Prompts'));
const PlatformAudit        = lazy(() => import('./pages/admin/platform/Audit'));

// Dev-only — visual smoke gallery for the UI design-system atoms.
// Mounted at /dev/atoms below behind `import.meta.env.DEV`; tree-shaken
// out of production builds.
const AtomsPage            = lazy(() => import('./components/dev/AtomsPage'));

function Guard({ children }: { children: React.ReactNode }) {
  const token = useAuth(s => s.token);
  if (!token) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function SuspenseLayout({ children }: { children: React.ReactNode }) {
  return (
    <Suspense
      fallback={
        <div
          className="min-h-screen flex items-center justify-center text-mn-ink-3 font-mono text-xs"
          style={{ letterSpacing: 'var(--tracking-caps)' }}
        >
          loading…
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

// Wraps the AppShell in the auth Guard so every authenticated route shares
// one shell + one Suspense boundary. The <Outlet /> inside AppShell renders
// the child route's element.
function AuthedShell() {
  return (
    <Guard>
      <AppShell />
    </Guard>
  );
}

export const router = createBrowserRouter([
  { path: '/login',    element: <SuspenseLayout><LoginPage /></SuspenseLayout> },
  { path: '/register', element: <SuspenseLayout><RegisterPage /></SuspenseLayout> },

  // Workspace invite acceptance — public (no auth) so invited users land here cold.
  { path: '/accept-invite', element: <SuspenseLayout><AcceptInvitePage /></SuspenseLayout> },

  // Public share-link viewer — outside the Guard.
  { path: '/m/:shareToken', element: <SuspenseLayout><PublicMeetingViewer /></SuspenseLayout> },

  // Dev-only atom gallery — present in dev builds only.
  ...(import.meta.env.DEV
    ? [
        {
          path: '/dev/atoms',
          element: (
            <SuspenseLayout>
              <AtomsPage />
            </SuspenseLayout>
          ),
        },
        // Dev preview: render MeetingReportPage outside the auth Guard so the
        // mock-mode (`?mock=1`) layout can be inspected without registering.
        {
          path: '/dev/meeting-preview/:meetingId',
          element: (
            <SuspenseLayout>
              <MeetingReportPage />
            </SuspenseLayout>
          ),
        },
      ]
    : []),

  // Everything else lives inside the AppShell + Guard.
  {
    element: <SuspenseLayout><AuthedShell /></SuspenseLayout>,
    children: [
      { path: '/',                          element: <DashboardPage /> },
      { path: '/projects',                  element: <ProjectsPage /> },
      // Wave 6 — new URL-routed project detail. The nested subtabs
      // (/projects/:id/meetings, /actions, /decisions, /risks, /people, /files,
      // /ask, /activity) are owned by ProjectDetailPage's internal <Routes>.
      { path: '/projects/:id/*',            element: <ProjectDetailPage /> },
      // Legacy /projects/:projectId — bookmark survival, redirects to new shape.
      { path: '/projects/:projectId',       element: <ProjectMeetingsPage /> },
      { path: '/upload',                    element: <MeetingUploadPage /> },
      { path: '/meetings',                  element: <MeetingsListPage /> },
      { path: '/meetings/:meetingId',       element: <MeetingReportPage /> },
      // Wave 10 — Tasks aggregation (mirrors PlanAI action-items IA).
      { path: '/tasks',                     element: <TasksPage /> },
      { path: '/search',                    element: <SearchPage /> },
      // Wave 6 — Ask NotoLogic lives at /ask. /chat survives as a redirect.
      { path: '/ask',                       element: <AskPage /> },
      { path: '/chat',                      element: <Navigate to="/ask" replace /> },
      { path: '/inbox',                     element: <InboxPage /> },
      // Personal settings. /settings renders the two-column shell (sub-nav +
      // white-paper card) and redirects to /settings/profile. Old org/platform
      // sub-paths now live in their own areas — redirected here so bookmarks
      // and in-app deep links keep working.
      {
        path: '/settings',
        element: <SettingsPage />,
        children: [
          { path: 'profile',       element: <SettingsProfile /> },
          { path: 'ai',            element: <SettingsAI /> },
          { path: 'tts',           element: <SettingsTTS /> },
          { path: 'notifications', element: <SettingsNotifications /> },
          { path: 'sessions',      element: <SettingsSessions /> },
          { path: 'integrations',  element: <SettingsIntegrations /> },
          // Moved → /admin/* (workspace admin)
          { path: 'org',           element: <Navigate to="/admin/workspace" replace /> },
          { path: 'members',       element: <Navigate to="/admin/members" replace /> },
          { path: 'billing',       element: <Navigate to="/admin/billing" replace /> },
          { path: 'audit',         element: <Navigate to="/admin/audit" replace /> },
          { path: 'developer',     element: <Navigate to="/admin/developer" replace /> },
          // Moved → /platform/* (platform admin)
          { path: 'platform/feature-flags', element: <Navigate to="/platform/feature-flags" replace /> },
          { path: 'platform/tts-providers', element: <Navigate to="/platform/tts-providers" replace /> },
          { path: 'platform/stt-providers', element: <Navigate to="/platform/stt-providers" replace /> },
          { path: 'platform/embeddings',    element: <Navigate to="/platform/embeddings" replace /> },
        ],
      },

      // Workspace (org) admin — its OWN left panel. <AdminLayout /> bounces
      // users below org-admin back to '/' and renders an <Outlet /> in the card.
      {
        path: '/admin',
        element: <AdminLayout />,
        children: [
          { index: true,           element: <AdminDashboard /> },
          { path: 'workspace',     element: <SettingsOrg /> },
          { path: 'members',       element: <SettingsMembers /> },
          { path: 'roles',         element: <AdminRoles /> },
          { path: 'usage',         element: <AdminUsage /> },
          { path: 'meetings',      element: <AdminMeetings /> },
          { path: 'users',         element: <AdminUsers /> },
          { path: 'ai',            element: <AdminAISettings /> },
          { path: 'billing',       element: <SettingsBilling /> },
          { path: 'audit',         element: <SettingsAudit /> },
          { path: 'developer',     element: <SettingsDeveloper /> },
          // Old platform sub-paths under /admin → moved to /platform/*
          { path: 'platform/tenants', element: <Navigate to="/platform" replace /> },
          { path: 'platform/ai-cost', element: <Navigate to="/platform/ai-cost" replace /> },
          { path: 'platform/health',  element: <Navigate to="/platform/health" replace /> },
          { path: 'platform/jobs',    element: <Navigate to="/platform/jobs" replace /> },
          { path: 'platform/flags',   element: <Navigate to="/platform/feature-flags" replace /> },
          { path: 'platform/prompts', element: <Navigate to="/platform/prompts" replace /> },
          { path: 'platform/audit',   element: <Navigate to="/platform/audit" replace /> },
          { path: 'tenants',          element: <Navigate to="/platform" replace /> },
          { path: 'health',           element: <Navigate to="/platform/health" replace /> },
        ],
      },

      // Platform admin — its OWN left panel. <PlatformLayout /> bounces
      // non-super-admins back to '/'. /platform → Tenants (the index).
      {
        path: '/platform',
        element: <PlatformLayout />,
        children: [
          { index: true,            element: <PlatformTenants /> },
          { path: 'ai-cost',        element: <PlatformAICost /> },
          { path: 'prompts',        element: <PlatformPrompts /> },
          { path: 'embeddings',     element: <PlatformEmbeddings /> },
          { path: 'tts-providers',  element: <PlatformTTSProviders /> },
          { path: 'stt-providers',  element: <PlatformSTTProviders /> },
          { path: 'feature-flags',  element: <PlatformFeatureFlags /> },
          { path: 'health',         element: <PlatformHealth /> },
          { path: 'jobs',           element: <PlatformJobs /> },
          { path: 'audit',          element: <PlatformAudit /> },
        ],
      },
    ],
  },

  { path: '*', element: <Navigate to="/" replace /> },
]);
